import { useEffect, useState } from "react";
import { useSEO } from "@/hooks/useSEO";
import { useNavigate } from "react-router-dom";
import { Flame, Zap, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, isOut, money, slug } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { GameCover, PlatformBadge } from "@/components/store/GameCover";
import { Skeleton } from "@/components/ui/skeleton";
import { BulkDeal } from "@/components/store/BulkDeal";
import { OfferCountdown } from "@/components/store/OfferCountdown";

const OfferCard = ({ game, index }) => {
  const { add, setOpen } = useCart();
  const navigate = useNavigate();
  const out = isOut(game);
  const id = slug(game.name);
  const save = game.compare_at && game.compare_at > game.price ? Math.round((1 - game.price / game.compare_at) * 100) : null;
  return (
    <div data-testid={`offer-card-${id}`} className="rise grid md:grid-cols-[1.3fr_1fr] rounded-xl border border-amber-500/40 bg-[#0F1117] overflow-hidden hover:shadow-[0_0_40px_rgba(245,158,11,0.15)] transition-shadow duration-300" style={{ animationDelay: `${index * 80}ms` }}>
      <div className="relative aspect-[460/215] md:aspect-auto md:min-h-[320px]">
        <GameCover game={game} />
        {save && <span data-testid={`offer-save-${id}`} className="absolute top-4 left-4 font-mono font-bold bg-amber-500 text-[#08090C] px-3 py-1 rounded">-{save}%</span>}
      </div>
      <div className="p-5 sm:p-8 flex flex-col gap-5">
        <div className="flex items-center gap-3"><PlatformBadge platform={game.platform} /><span className="text-xs text-emerald-400">{out ? "Sold out" : "In stock"}</span></div>
        <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl leading-tight">{game.name}</h2>
        <p className="text-sm text-gray-400">{game.description}</p>
        <div className="flex items-end gap-3">
          <span data-testid={`offer-price-${id}`} className="font-mono font-bold text-5xl text-amber-400">{money(game.price)}</span>
          {game.compare_at && <span className="font-mono text-gray-500 line-through mb-2">{money(game.compare_at)}</span>}
          <span className="text-xs text-gray-500 mb-2">each</span>
        </div>
        <BulkDeal game={game} />
        <div className="grid grid-cols-2 gap-3 mt-auto">
          <button data-testid={`offer-buy-${id}`} disabled={out} onClick={() => { add(game); navigate("/checkout"); }}
            className="h-12 rounded-md bg-amber-500 text-[#08090C] font-bold inline-flex items-center justify-center gap-2 hover:bg-amber-400 disabled:opacity-40 transition-colors"><Zap size={16} /> Buy now</button>
          <button data-testid={`offer-add-${id}`} disabled={out} onClick={() => { add(game); toast.success(`${game.name} added`, { action: { label: "View cart", onClick: () => setOpen(true) } }); }}
            className="h-12 rounded-md border border-white/15 font-semibold inline-flex items-center justify-center gap-2 hover:border-amber-500/60 disabled:opacity-40 transition-colors"><ShoppingBag size={16} /> Add to cart</button>
        </div>
      </div>
    </div>
  );
};

export default function Offers() {
  useSEO({ title: "Offer of the Week", description: "This week's hand-picked game key deal at an unbeatable price. Pay with USDT, code delivered by email.", path: "/offers" });
  const [offers, setOffers] = useState(null);
  useEffect(() => { api.get("/offers").then((r) => setOffers(r.data)); }, []);
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-10 space-y-10">
      <div className="rise flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="label-eyebrow text-amber-400 flex items-center gap-2"><Flame size={14} /> Offer of the week</p>
          <h1 className="font-display font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02]">This week's steal.</h1>
          <p className="text-gray-400 mt-4 max-w-xl">Hand-picked deals at prices you won't see anywhere else — plus bulk pricing for resellers and big orders.</p>
        </div>
        <OfferCountdown />
      </div>
      <div data-testid="offers-list" className="space-y-6">
        {!offers ? <Skeleton className="h-80 bg-white/5" />
          : offers.length === 0 ? <p data-testid="offers-empty" className="text-gray-400 py-16 text-center">No offers this week — check back soon.</p>
          : offers.map((g, i) => <OfferCard key={g.id} game={g} index={i} />)}
      </div>
    </div>
  );
}
